import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE } from '@core/http/http-context-tokens';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import {
  CreateNewsletterRequestDto,
  NewsletterDto,
  PublishNewsletterRequestDto,
  UpdateNewsletterRequestDto,
} from '../../dtos/newsletter.dto';

@Injectable()
export class NewslettersRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public list(organizationId: number): Observable<NewsletterDto[]> {
    return this.http.get<NewsletterDto[]>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.list(organizationId)}`,
    );
  }

  public getById(organizationId: number, newsletterId: string): Observable<NewsletterDto> {
    return this.http.get<NewsletterDto>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.byId(organizationId, newsletterId)}`,
    );
  }

  public create(
    organizationId: number,
    payload: CreateNewsletterRequestDto,
  ): Observable<NewsletterDto> {
    return this.http.post<NewsletterDto>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.list(organizationId)}`,
      payload,
    );
  }

  public update(
    organizationId: number,
    newsletterId: string,
    payload: UpdateNewsletterRequestDto,
  ): Observable<NewsletterDto> {
    return this.http.put<NewsletterDto>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.byId(organizationId, newsletterId)}`,
      payload,
    );
  }

  public delete(organizationId: number, newsletterId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.byId(organizationId, newsletterId)}`,
    );
  }

  // Its caller explains a failure itself -- a push the backend refuses has a message of its own -- so
  // the generic error toast must not stack a misleading "unexpected problem" on top. An outage
  // still gets the global toast, since there is nothing to render inline for it.
  public publish(
    organizationId: number,
    newsletterId: string,
    payload: PublishNewsletterRequestDto,
  ): Observable<NewsletterDto> {
    return this.http.post<NewsletterDto>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.publish(organizationId, newsletterId)}`,
      payload,
      { context: new HttpContext().set(SUPPRESS_ERROR_TOAST_EXCEPT_SERVICE_UNAVAILABLE, true) },
    );
  }

  public archive(organizationId: number, newsletterId: string): Observable<NewsletterDto> {
    return this.http.post<NewsletterDto>(
      `${this.apiUrl}${API_ENDPOINTS.newsletters.archive(organizationId, newsletterId)}`,
      {},
    );
  }
}
